import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import { makeStyles } from 'tss-react/mui';

import { filtersSelectors } from 'app-components/flux/filters';
import type {
  ChartOfAccountFilterItemProps,
  FilterItemProps,
  PayeeFilterItemProps,
  TagFilterItemProps,
} from 'app-components/flux/filters/filtersTypes';
import { rootUiActions } from 'app-components/flux/root-ui';
import { spendingWatchlistSelectors } from 'app-components/flux/spending-watchlist';
import { getLogger } from 'app-components/utils/core';

import { buildEmptyFilterItems } from 'app-components/forms/watchlist/watchlistForm';

import StdDialog from 'components/Dialogs/StdDialog';
import WatchlistForm from 'components/Dialogs/WatchlistModal/WatchlistForm';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

type FilterType = 'categories' | 'payees' | 'tags';
type FormFilterType = 'CHARTOFACCOUNT' | 'PAYEE' | 'TAG';

type CategoryItems = ChartOfAccountFilterItemProps['chartOfAccounts'];
type PayeeItems = PayeeFilterItemProps['payees'];
type TagItems = TagFilterItemProps['tags'];

type UpsertWatchlistData = {
  watchlistId?: string;
  name?: string;
  filterType?: FilterType;
  filterItems?: CategoryItems | PayeeItems | TagItems;
  targetAmount?: number;
  emoji?: string;
};

type LlmWatchlistOperation = 'create' | 'update';

type LlmWatchlistDialogProps = {
  dialogId: string;
  operation: LlmWatchlistOperation;
  data: UpsertWatchlistData;
  callback?: (result: { success: boolean; watchlistId?: string; error?: string }) => void;
};

type WatchlistFormDefaultSubmit = (values: unknown, options?: { showSuccessModal?: boolean }) => void;

const normalizeEmoji = (emoji?: string) => {
  if (!emoji) return '';

  // If it already looks like our stored format (hex or hex-hex), keep as-is.
  if (/^[0-9a-fA-F-]+$/.test(emoji)) {
    return emoji;
  }

  const codePoints: string[] = [];
  for (const char of Array.from(emoji)) {
    const cp = char.codePointAt(0);
    if (cp != null) {
      codePoints.push(cp.toString(16));
    }
  }

  return codePoints.join('-');
};

export const DIALOG_TYPE_LLM_WATCHLIST = 'LLM_WATCHLIST';

const log = getLogger('components/Dialogs/LlmWatchlistDialog');
const useStyles = makeStyles()(() => ({
  root: {
    position: 'relative',
    minHeight: 320,
  },
}));

const typesData = Object.freeze({
  CHARTOFACCOUNT: ['Category', 'Categories', 'categories'],
  PAYEE: ['Payee', 'Payees', 'payeeNames'],
  TAG: ['Tag', 'Tags', 'tags'],
});

const filterKeyObj: Record<FormFilterType, 'chartOfAccounts' | 'payees' | 'tags'> = {
  CHARTOFACCOUNT: 'chartOfAccounts',
  PAYEE: 'payees',
  TAG: 'tags',
};

const llmFilterTypeToFormType: Record<FilterType, FormFilterType> = {
  categories: 'CHARTOFACCOUNT',
  payees: 'PAYEE',
  tags: 'TAG',
};

const getInitialFilterItems = (
  filterType: FilterType | undefined,
  filterItems: CategoryItems | PayeeItems | TagItems | undefined,
): ReturnType<typeof buildEmptyFilterItems> => {
  switch (filterType) {
    case 'categories': {
      const items = filterItems as CategoryItems | undefined;
      return items?.length
        ? { type: 'CHARTOFACCOUNT', chartOfAccounts: items }
        : buildEmptyFilterItems('CHARTOFACCOUNT');
    }
    case 'payees': {
      const items = filterItems as PayeeItems | undefined;
      return items?.length ? { type: 'PAYEE', payees: items } : buildEmptyFilterItems('PAYEE');
    }
    case 'tags': {
      const items = filterItems as TagItems | undefined;
      return items?.length ? { type: 'TAG', tags: items } : buildEmptyFilterItems('TAG');
    }
    default:
      return buildEmptyFilterItems('PAYEE');
  }
};

const getItemsForFormType = (
  formType: FormFilterType,
  filterItem: FilterItemProps | undefined,
): CategoryItems | PayeeItems | TagItems => {
  switch (formType) {
    case 'CHARTOFACCOUNT':
      return filterItem?.type === 'CHARTOFACCOUNT' ? filterItem.chartOfAccounts : [];
    case 'PAYEE':
      return filterItem?.type === 'PAYEE' ? filterItem.payees : [];
    case 'TAG':
      return filterItem?.type === 'TAG' ? filterItem.tags : [];
    default:
      return [];
  }
};

const LlmWatchlistDialog = ({ dialogId, operation, data, callback }: LlmWatchlistDialogProps) => {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  log.debug({ data });

  const isEditing = operation === 'update' && Boolean(data.watchlistId);
  const hasNameOverride = data.name !== undefined;
  const hasEmojiOverride = data.emoji !== undefined;
  const hasTargetAmountOverride = data.targetAmount !== undefined;
  const hasFilterTypeOverride = data.filterType !== undefined;
  const hasFilterItemsOverride = data.filterItems !== undefined;

  const filters = useAppSelector(filtersSelectors.getFilters);
  const existingWatchlist = useAppSelector((state) =>
    isEditing && data.watchlistId
      ? spendingWatchlistSelectors.getSpendingWatchListById(state as any, data.watchlistId)
      : undefined,
  );
  const baseWatchlist = useMemo(() => {
    if (!existingWatchlist) return undefined;
    return typeof (existingWatchlist as any)?.toJS === 'function'
      ? (existingWatchlist as any).toJS()
      : existingWatchlist;
  }, [existingWatchlist]);

  const existingFilter = useMemo(() => {
    if (!isEditing || !existingWatchlist?.filterId) return null;
    return filters.find((f) => f.id === existingWatchlist.filterId) || null;
  }, [filters, isEditing, existingWatchlist]);

  const formFilterType = useMemo<FormFilterType | null>(
    () =>
      data.filterType
        ? llmFilterTypeToFormType[data.filterType]
        : isEditing
          ? (() => {
              const t = existingFilter?.filterItems?.[0]?.type;
              if (t === 'CHARTOFACCOUNT' || t === 'PAYEE' || t === 'TAG') {
                return t;
              }
              return null;
            })()
          : 'PAYEE',
    [data.filterType, existingFilter, isEditing],
  );

  const initialFilterItems = useMemo(
    () => getInitialFilterItems(data.filterType, data.filterItems),
    [data.filterItems, data.filterType],
  );

  const overwriteInitialData = useMemo(() => {
    if (!formFilterType) return undefined;
    if (isEditing && !baseWatchlist) return undefined;

    if (
      isEditing &&
      !hasNameOverride &&
      !hasEmojiOverride &&
      !hasTargetAmountOverride &&
      !hasFilterTypeOverride &&
      !hasFilterItemsOverride
    ) {
      return undefined;
    }

    const key = filterKeyObj[formFilterType];
    const existingFilterItems = getItemsForFormType(formFilterType, existingFilter?.filterItems?.[0]);
    const createFilterItems = getItemsForFormType(formFilterType, initialFilterItems);
    const resolvedFilterItems = isEditing && !hasFilterItemsOverride ? existingFilterItems : createFilterItems;

    const resolvedName = isEditing
      ? hasNameOverride
        ? (data.name ?? '')
        : (baseWatchlist?.name ?? '')
      : (data.name ?? '');
    const resolvedEmoji = isEditing
      ? hasEmojiOverride
        ? normalizeEmoji(data.emoji)
        : normalizeEmoji(baseWatchlist?.emoji)
      : normalizeEmoji(data.emoji);
    const resolvedTargetAmount = isEditing
      ? hasTargetAmountOverride
        ? (data.targetAmount ?? null)
        : baseWatchlist?.targetAmount
      : data.targetAmount;

    return {
      name: resolvedName,
      emoji: resolvedEmoji ?? '',
      targetAmount: resolvedTargetAmount,
      showTargetAmount: Boolean(resolvedTargetAmount != null).toString(),
      ...(isEditing
        ? {
            timeRangeEnabled: baseWatchlist?.period === 'FIXED',
            startDate:
              baseWatchlist?.period === 'FIXED' && baseWatchlist?.startDate
                ? DateTime.fromISO(baseWatchlist.startDate).toISODate()
                : null,
            endDate:
              baseWatchlist?.period === 'FIXED' && baseWatchlist?.endDate
                ? DateTime.fromISO(baseWatchlist.endDate).toISODate()
                : null,
          }
        : {}),
      filter: {
        type: formFilterType,
        [key]: resolvedFilterItems,
      },
    };
  }, [
    data.emoji,
    data.name,
    data.targetAmount,
    baseWatchlist,
    existingFilter,
    formFilterType,
    hasEmojiOverride,
    hasFilterItemsOverride,
    hasFilterTypeOverride,
    hasNameOverride,
    hasTargetAmountOverride,
    initialFilterItems,
    isEditing,
  ]);

  const [formData, setFormData] = useState<{
    isDisabled: boolean;
    submitForm: () => void;
  } | null>(null);

  const setFormDataWrapper = useCallback((updatedFormData: typeof formData) => {
    setFormData((prev) => {
      if (!prev || prev.isDisabled !== updatedFormData?.isDisabled || prev.submitForm !== updatedFormData?.submitForm) {
        return updatedFormData;
      }
      return prev;
    });
  }, []);

  const closeDialog = useCallback(() => {
    dispatch(rootUiActions.removeDialog(dialogId));
  }, [dispatch, dialogId]);

  const handleCancel = useCallback(() => {
    callback?.({ success: false, error: 'User cancelled' });
    closeDialog();
  }, [callback, closeDialog]);

  const handleSubmit = useCallback(
    (values: unknown, submitDefault: WatchlistFormDefaultSubmit) => {
      submitDefault(values, { showSuccessModal: false });
      callback?.({ success: true, watchlistId: data.watchlistId });
      closeDialog();
    },
    [callback, closeDialog, data.watchlistId],
  );

  const memoForm = useMemo(() => {
    const filterForForm = isEditing ? existingFilter : formFilterType;
    const canRenderForm = isEditing ? Boolean(filterForForm && baseWatchlist) : Boolean(filterForForm);

    return canRenderForm ? (
      <WatchlistForm
        typesData={typesData}
        watchlist={baseWatchlist}
        isEditing={isEditing}
        filter={filterForForm}
        setFormData={setFormDataWrapper}
        overwriteInitialData={overwriteInitialData}
        onSubmit={handleSubmit}
      />
    ) : null;
  }, [
    baseWatchlist,
    existingFilter,
    formFilterType,
    handleSubmit,
    isEditing,
    overwriteInitialData,
    setFormDataWrapper,
  ]);

  return (
    <StdDialog
      onClose={handleCancel}
      open
      showCloseButton
      size='md'
      title={`${isEditing ? 'Edit' : 'Create a'} Watchlist`}
    >
      <DialogContent className={classes.root}>{memoForm}</DialogContent>
      {memoForm && (
        <>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant='contained'
              disabled={formData?.isDisabled ?? true}
              onClick={() => formData?.submitForm?.()}
            >
              {isEditing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </>
      )}
    </StdDialog>
  );
};

export default LlmWatchlistDialog;
